import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
} from "react";
import { useUserPreferences } from "../contexts/UserPreferencesContext";
import en from "./en";
import fr from "./fr";

// Supported locales
export const LOCALES = {
	ENGLISH: "en",
	FRENCH: "fr",
};

// Translation dictionaries
const translations = {
	[LOCALES.ENGLISH]: en,
	[LOCALES.FRENCH]: fr,
};

// Utility function to detect the preferred language
export const detectPreferredLocale = () => {
	// 1. Check localStorage (saved user preferences)
	const savedLocale = localStorage.getItem("userPreferences");
	if (savedLocale) {
		try {
			const prefs = JSON.parse(savedLocale);
			if (prefs.locale && Object.values(LOCALES).includes(prefs.locale)) {
				return prefs.locale;
			}
		} catch (e) {
			console.warn("Error parsing saved locale preferences:", e);
		}
	}

	// 2. Use the browser's language
	const browserLocale = navigator.language || navigator.languages?.[0];
	if (browserLocale) {
		const shortLocale = browserLocale.split("-")[0];
		if (Object.values(LOCALES).includes(shortLocale)) {
			return shortLocale;
		}
	}

	// 3. Default language
	return LOCALES.ENGLISH;
};

// React context
const I18nContext = createContext();

// Utility function for variable interpolation
const interpolate = (str, variables = {}) =>
	Object.entries(variables).reduce(
		(result, [key, value]) => result.replaceAll(`{{${key}}}`, value),
		str
	);

// React provider to wrap the app
export const I18nProvider = ({ children, locale: propLocale }) => {
	const userPrefs = useUserPreferences();
	const userLocale = userPrefs?.locale;

	// Determine the initial locale based on priority:
	// 1. Prop locale (explicit override)
	// 2. User preferences context
	// 3. Auto-detected locale
	const getInitialLocale = useCallback(() => {
		if (propLocale && Object.values(LOCALES).includes(propLocale)) {
			return propLocale;
		}
		if (userLocale && Object.values(LOCALES).includes(userLocale)) {
			return userLocale;
		}
		return detectPreferredLocale();
	}, [propLocale, userLocale]);

	const [currentLocale, setCurrentLocale] = useState(getInitialLocale);

	// Update locale when user preferences change
	useEffect(() => {
		const newLocale = getInitialLocale();
		if (newLocale !== currentLocale) {
			setCurrentLocale(newLocale);
		}
	}, [propLocale, userLocale, currentLocale, getInitialLocale]);

	const translate = useCallback(
		(key, variables = {}) => {
			const { count } = variables;
			const baseTranslations = translations[currentLocale] || {};

			// Handle pluralization if count is a number
			const pluralKey =
				typeof count === "number" && count > 1 ? `${key}_plural` : key;
			const translation =
				baseTranslations[pluralKey] || baseTranslations[key] || key;

			return interpolate(translation, variables);
		},
		[currentLocale]
	);

	return (
		<I18nContext.Provider value={{ translate, currentLocale }}>
			{children}
		</I18nContext.Provider>
	);
};

// React hook to access translations
export const useTranslation = () => {
	const context = useContext(I18nContext);
	if (!context) {
		throw new Error("useTranslation must be used within an I18nProvider");
	}
	return context;
};

// Utility function for early translation (e.g. in Loader before context is available)
export const getTranslations = locale => {
	const safeLocale = Object.values(LOCALES).includes(locale)
		? locale
		: detectPreferredLocale();

	const translate = (key, variables = {}) => {
		const { count } = variables;
		const baseTranslations = translations[safeLocale] || {};
		const pluralKey =
			typeof count === "number" && count > 1 ? `${key}_plural` : key;
		const translation =
			baseTranslations[pluralKey] || baseTranslations[key] || key;
		return interpolate(translation, variables);
	};

	return { translate, locale: safeLocale };
};
